import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import AdminLayout from '../../components/AdminLayout'
import { supabase } from '../../lib/supabase'

const fmt = (n, d = 2) => Number(n || 0).toLocaleString('es-UY', { minimumFractionDigits: d, maximumFractionDigits: d })

export default function Multimoneda() {
  const [tab, setTab] = useState('saldos_usd')
  return (
    <AdminLayout>
      <div className="mm-wrap">
        <div className="mm-headline">
          <div className="mm-headline-left">
            <h1>Multimoneda · USD</h1>
            <p>
              Revaluación de saldos en USD y generación de asientos en moneda extranjera. La contabilidad uruguaya se lleva en UYU.
              <br /><b>Referencia para los asientos (Roberto criterio #1):</b> TC interbancario billete <b>comprador</b> del día <b>anterior</b> a la operación.
            </p>
          </div>
          <Link to="/admin/finanzas/tipos-cambio" className="mm-link-tc" title="Ir a la página de cotizaciones BCU">
            💵 Tipos de cambio →
          </Link>
        </div>
        <div className="mm-tabs">
          {['saldos_usd', 'crear_asiento'].map(t => (
            <button key={t} className={`mm-tab ${tab === t ? 'on' : ''}`} onClick={() => setTab(t)}>
              {t === 'saldos_usd' ? '💵 Saldos en USD' : '✍️ Asiento en USD'}
            </button>
          ))}
        </div>
        {tab === 'saldos_usd' && <SaldosUSD />}
        {tab === 'crear_asiento' && <CrearAsientoUSD />}
      </div>
      <style>{CSS}</style>
    </AdminLayout>
  )
}


function SaldosUSD() {
  const [saldos, setSaldos] = useState([])
  const [tcCierre, setTcCierre] = useState(null)
  const [loading, setLoading] = useState(true)
  const [ajustando, setAjustando] = useState(null)
  const [fechaCierre, setFechaCierre] = useState(new Date().toISOString().slice(0, 10))

  useEffect(() => { cargar() }, [fechaCierre])
  async function cargar() {
    setLoading(true)
    const { data: s } = await supabase.from('v_cuentas_saldos_usd').select('*')
    setSaldos(s || [])
    const { data: tc } = await supabase.from('tipos_cambio_bcu').select('tc_interbancario_comprador').eq('codigo_iso', 'USD').lte('fecha', fechaCierre).order('fecha', { ascending: false }).limit(1).maybeSingle()
    setTcCierre(tc?.tc_interbancario_comprador || null)
    setLoading(false)
  }

  async function ajustar(cuenta) {
    if (!confirm(`¿Generar asiento de diferencia de cambio para ${cuenta} al ${fechaCierre}?`)) return
    setAjustando(cuenta)
    try {
      const { data, error } = await supabase.rpc('ajuste_diferencia_cambio', { p_fecha: fechaCierre, p_cuenta_codigo: cuenta })
      if (error) throw error
      if (data.sin_saldo) alert('No hay saldo USD en esa cuenta.')
      else if (data.sin_diferencia) alert('No hay diferencia significativa (< $0.01).')
      else alert(`✓ Diferencia ${data.diferencia > 0 ? '+' : '-'}$${fmt(Math.abs(data.diferencia))} al TC ${data.tc_cierre}`)
      cargar()
    } catch (e) { alert('Error: ' + e.message) }
    finally { setAjustando(null) }
  }

  return (
    <div>
      <div className="mm-toolbar">
        <label><span>Fecha de cierre</span><input type="date" value={fechaCierre} onChange={e => setFechaCierre(e.target.value)} /></label>
        {tcCierre && <div className="mm-tc-pill">★ TC contable (interbanc. comprador): <b>${fmt(tcCierre, 4)}</b></div>}
      </div>

      {loading ? <div className="mm-loading">Cargando…</div> :
       saldos.length === 0 ? (
        <div className="mm-empty">
          <strong>No hay saldos en USD</strong>
          <p>Las cuentas se listarán acá cuando haya asientos en moneda extranjera con saldo distinto de cero.</p>
        </div>
      ) : (
        <div className="mm-tabla-wrap">
          <table className="mm-tabla">
            <thead><tr>
              <th>Cuenta</th><th>Nombre</th>
              <th style={{ textAlign: 'right' }}>Saldo USD</th>
              <th style={{ textAlign: 'right' }}>Saldo UYU contable</th>
              <th style={{ textAlign: 'right' }}>Saldo UYU revaluado</th>
              <th style={{ textAlign: 'right' }}>Diferencia</th>
              <th>Movs</th><th></th>
            </tr></thead>
            <tbody>
              {saldos.map(s => {
                const revaluado = (s.saldo_usd || 0) * (tcCierre || 0)
                const dif = revaluado - (s.saldo_uyu_actual || 0)
                return (
                  <tr key={s.cuenta_codigo}>
                    <td><code>{s.cuenta_codigo}</code></td>
                    <td>{s.nombre}</td>
                    <td style={{ textAlign: 'right', fontWeight: 700 }}>USD {fmt(s.saldo_usd)}</td>
                    <td style={{ textAlign: 'right' }}>${fmt(s.saldo_uyu_actual)}</td>
                    <td style={{ textAlign: 'right', color: '#1A3461' }}>${fmt(revaluado)}</td>
                    <td style={{ textAlign: 'right', fontWeight: 800, color: dif >= 0 ? '#16a34a' : '#dc2626' }}>{dif >= 0 ? '+' : ''}${fmt(dif)}</td>
                    <td style={{ fontSize: 11, color: '#64748b' }}>{s.cant_movimientos}</td>
                    <td><button className="mm-btn-ghost" onClick={() => ajustar(s.cuenta_codigo)} disabled={ajustando === s.cuenta_codigo || Math.abs(dif) < 0.01}>{ajustando === s.cuenta_codigo ? '…' : 'Ajustar'}</button></td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      <div className="mm-info">
        <strong>📚 Diferencias de cambio</strong>
        <ul>
          <li><b>Cuándo:</b> en cierre mensual o cuando el TC se mueve significativamente.</li>
          <li><b>Cómo:</b> el sistema toma el saldo USD, lo revaloriza al TC de cierre y compara con el saldo UYU registrado.</li>
          <li><b>Diferencia positiva</b> en pasivo USD = pérdida (cuenta 5.7.01). <b>Diferencia negativa</b> = ganancia (cuenta 4.6.01).</li>
          <li><b>Asiento generado</b> ajusta la cuenta original y carga la pérdida/ganancia al resultado.</li>
        </ul>
      </div>
    </div>
  )
}

function CrearAsientoUSD() {
  const [cuentas, setCuentas] = useState([])
  const [form, setForm] = useState({
    fecha: new Date().toISOString().slice(0, 10),
    descripcion: '',
    tipo_cambio: '',
    lineas: [
      { cuenta_codigo: '', debe_origen: 0, haber_origen: 0, detalle: '' },
      { cuenta_codigo: '', debe_origen: 0, haber_origen: 0, detalle: '' },
    ],
  })
  const [tcSugerido, setTcSugerido] = useState(null)
  const [creando, setCreando] = useState(false)

  useEffect(() => {
    supabase.from('cuentas_contables').select('codigo, nombre').eq('es_imputable', true).order('codigo').then(({ data }) => setCuentas(data || []))
  }, [])
  useEffect(() => {
    if (!form.fecha) return
    supabase.rpc('tc_para_fecha', { p_fecha: form.fecha, p_moneda: 'USD' }).then(({ data }) => setTcSugerido(data))
  }, [form.fecha])

  const totales = useMemo(() => {
    const td = form.lineas.reduce((s, l) => s + Number(l.debe_origen || 0), 0)
    const th = form.lineas.reduce((s, l) => s + Number(l.haber_origen || 0), 0)
    const tc = Number(form.tipo_cambio || tcSugerido || 0)
    return { td, th, balanceado: Math.abs(td - th) < 0.01 && td > 0, td_uyu: td * tc, th_uyu: th * tc }
  }, [form.lineas, form.tipo_cambio, tcSugerido])

  function updateLinea(i, k, v) {
    const nuevas = [...form.lineas]; nuevas[i] = { ...nuevas[i], [k]: v }; setForm({ ...form, lineas: nuevas })
  }
  function addLinea() { setForm({ ...form, lineas: [...form.lineas, { cuenta_codigo: '', debe_origen: 0, haber_origen: 0, detalle: '' }] }) }
  function removeLinea(i) { setForm({ ...form, lineas: form.lineas.filter((_, x) => x !== i) }) }

  async function guardar(e) {
    e.preventDefault()
    if (!totales.balanceado) { alert('El asiento no está balanceado.'); return }
    setCreando(true)
    try {
      const { error } = await supabase.rpc('crear_asiento_multimoneda', {
        p_fecha: form.fecha, p_descripcion: form.descripcion, p_moneda: 'USD',
        p_tipo_cambio: form.tipo_cambio ? Number(form.tipo_cambio) : null,
        p_origen_tabla: 'manual_multimoneda', p_origen_id: crypto.randomUUID(),
        p_lineas: form.lineas.filter(l => l.cuenta_codigo && (Number(l.debe_origen) > 0 || Number(l.haber_origen) > 0)),
      })
      if (error) throw error
      alert('✓ Asiento USD creado y convertido a UYU')
      setForm({ ...form, descripcion: '', lineas: [{ cuenta_codigo: '', debe_origen: 0, haber_origen: 0, detalle: '' }, { cuenta_codigo: '', debe_origen: 0, haber_origen: 0, detalle: '' }] })
    } catch (err) { alert('Error: ' + err.message) }
    finally { setCreando(false) }
  }

  return (
    <form className="mm-form" onSubmit={guardar}>
      <h3>Nuevo asiento en USD</h3>
      <div className="mm-form-row">
        <label><span>Fecha</span><input type="date" value={form.fecha} onChange={e => setForm({ ...form, fecha: e.target.value })} required /></label>
        <label style={{ flex: 2 }}><span>Descripción</span><input type="text" value={form.descripcion} onChange={e => setForm({ ...form, descripcion: e.target.value })} required placeholder="Ej: Pago hosting Cloudflare USD 50" /></label>
        <label><span>Tipo de cambio (opcional)</span><input type="number" step="0.0001" value={form.tipo_cambio} onChange={e => setForm({ ...form, tipo_cambio: e.target.value })} placeholder={tcSugerido ? `BCU: ${tcSugerido}` : '...'} /></label>
      </div>
      {tcSugerido && <div style={{ fontSize: 12, color: '#64748b', marginBottom: 8 }}>TC sugerido para {form.fecha}: <b style={{ color: '#1A3461' }}>${fmt(tcSugerido, 4)}</b> {form.tipo_cambio ? '(usando override)' : '(automático)'}</div>}

      <h4 className="mm-h4">Líneas (en USD)</h4>
      <div className="mm-lineas-wrap">
        <table className="mm-lineas">
          <thead><tr><th>Cuenta</th><th>Debe USD</th><th>Haber USD</th><th>Detalle</th><th></th></tr></thead>
          <tbody>
            {form.lineas.map((l, i) => (
              <tr key={i}>
                <td><select value={l.cuenta_codigo} onChange={e => updateLinea(i, 'cuenta_codigo', e.target.value)} required>
                  <option value="">— elegí cuenta —</option>
                  {cuentas.map(c => <option key={c.codigo} value={c.codigo}>{c.codigo} · {c.nombre}</option>)}
                </select></td>
                <td><input type="number" step="0.01" value={l.debe_origen} onChange={e => updateLinea(i, 'debe_origen', e.target.value)} /></td>
                <td><input type="number" step="0.01" value={l.haber_origen} onChange={e => updateLinea(i, 'haber_origen', e.target.value)} /></td>
                <td><input type="text" value={l.detalle} onChange={e => updateLinea(i, 'detalle', e.target.value)} placeholder="..." /></td>
                <td><button type="button" className="mm-x" onClick={() => removeLinea(i)} disabled={form.lineas.length <= 2}>×</button></td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td style={{ textAlign: 'right', fontWeight: 700 }}>TOTAL</td>
              <td style={{ fontWeight: 800, color: '#16a34a' }}>USD {fmt(totales.td)}</td>
              <td style={{ fontWeight: 800, color: '#dc2626' }}>USD {fmt(totales.th)}</td>
              <td colSpan={2}>{totales.balanceado ? <span className="mm-ok">✓ Balanceado</span> : <span className="mm-err">No balanceado · diferencia USD {fmt(Math.abs(totales.td - totales.th))}</span>}</td>
            </tr>
            <tr>
              <td style={{ textAlign: 'right', fontWeight: 700, fontSize: 11, color: '#64748b' }}>EN UYU</td>
              <td style={{ color: '#16a34a', fontSize: 12 }}>${fmt(totales.td_uyu)}</td>
              <td style={{ color: '#dc2626', fontSize: 12 }}>${fmt(totales.th_uyu)}</td>
              <td colSpan={2} style={{ fontSize: 11, color: '#64748b' }}>convertido al TC {form.tipo_cambio || tcSugerido || '?'}</td>
            </tr>
          </tfoot>
        </table>
        <button type="button" className="mm-btn-ghost" onClick={addLinea}>+ Agregar línea</button>
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 14 }}>
        <button type="submit" className="mm-btn-primary" disabled={creando || !totales.balanceado}>{creando ? 'Creando…' : 'Crear asiento'}</button>
      </div>
    </form>
  )
}

const CSS = `
.mm-wrap { padding: 16px 20px 32px; max-width: 1400px; margin: 0 auto }
.mm-headline { display: flex; justify-content: space-between; align-items: flex-start; gap: 14px; flex-wrap: wrap; margin-bottom: 14px }
.mm-headline-left { flex: 1; min-width: 260px }
.mm-headline h1 { font-size: 26px; font-weight: 900; color: #1A3461; margin: 0 0 4px; letter-spacing: -.02em }
.mm-headline p { font-size: 13px; color: #64748b; margin: 0 0 6px }
.mm-link-tc { display: inline-flex; align-items: center; gap: 6px; background: #1A3461; color: #fff; padding: 9px 14px; border-radius: 8px; font-size: 13px; font-weight: 800; text-decoration: none; font-family: inherit; white-space: nowrap }
.mm-link-tc:hover { background: #0f2147 }
.mm-tabs { display: flex; gap: 6px; border-bottom: 1px solid #e2e8f0; margin-bottom: 18px }
.mm-tab { background: transparent; border: 0; padding: 10px 16px; font-size: 13px; font-weight: 700; color: #94a3b8; cursor: pointer; border-bottom: 2px solid transparent; font-family: inherit }
.mm-tab.on { color: #1A3461; border-bottom-color: #1A3461 }
.mm-actual { background: linear-gradient(135deg, #1A3461 0%, #0f1f3d 100%); color: #fff; padding: 16px 22px; border-radius: 12px; margin-bottom: 18px }
.mm-actual-l { font-size: 11px; opacity: .7; text-transform: uppercase; letter-spacing: .06em; font-weight: 700 }
.mm-actual-row { display: flex; gap: 30px; margin: 8px 0 }
.mm-actual-row div { display: flex; flex-direction: column }
.mm-actual-row span { font-size: 11px; opacity: .7 }
.mm-actual-row b { font-size: 22px; font-weight: 900 }
.mm-arb b { color: #5BBDD8 }
.mm-actual-f { font-size: 11px; opacity: .6 }
.mm-form { background: #fff; border: 1px solid #e2e8f0; border-radius: 10px; padding: 16px 20px; margin-bottom: 16px }
.mm-form h3 { margin: 0 0 12px; color: #1A3461; font-weight: 800; font-size: 16px }
.mm-form-row { display: flex; gap: 10px; align-items: flex-end; flex-wrap: wrap }
.mm-form label { display: flex; flex-direction: column; gap: 4px; flex: 1; min-width: 120px }
.mm-form label span { font-size: 11px; font-weight: 700; color: #475569 }
.mm-form input, .mm-form select { padding: 8px 10px; border: 1px solid #cbd5e1; border-radius: 7px; font-size: 13px; font-family: inherit }
.mm-h4 { margin: 18px 0 8px; font-size: 13px; color: #475569; font-weight: 800 }
.mm-lineas-wrap { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 8px; padding: 10px; overflow: auto }
.mm-lineas { width: 100%; border-collapse: collapse; font-size: 12.5px }
.mm-lineas th { text-align: left; padding: 6px 8px; font-size: 10.5px; color: #64748b; text-transform: uppercase; font-weight: 700 }
.mm-lineas td { padding: 4px 6px }
.mm-lineas select, .mm-lineas input { width: 100%; padding: 6px 8px; border: 1px solid #cbd5e1; border-radius: 5px; font-size: 12.5px; font-family: inherit; background: #fff }
.mm-lineas tfoot td { border-top: 2px solid #cbd5e1; padding-top: 8px }
.mm-x { background: transparent; border: 1px solid #fee2e2; color: #dc2626; width: 24px; height: 24px; border-radius: 5px; cursor: pointer }
.mm-ok { color: #16a34a; font-weight: 800 }
.mm-err { color: #dc2626; font-weight: 700; font-size: 11.5px }
.mm-btn-primary { background: #1A3461; color: #fff; border: 0; padding: 9px 18px; border-radius: 8px; font-size: 13px; font-weight: 800; cursor: pointer; font-family: inherit }
.mm-btn-primary:disabled { opacity: .5; cursor: not-allowed }
.mm-btn-ghost { background: transparent; border: 1px solid #cbd5e1; color: #475569; padding: 6px 12px; border-radius: 6px; font-size: 12px; font-weight: 700; cursor: pointer; font-family: inherit; margin-top: 8px }
.mm-toolbar { display: flex; gap: 16px; align-items: flex-end; margin-bottom: 14px; flex-wrap: wrap }
.mm-tc-pill { background: #eff6ff; border: 1px solid #bfdbfe; color: #1e3a8a; padding: 8px 14px; border-radius: 999px; font-size: 13px }
.mm-tabla-wrap { background: #fff; border: 1px solid #e2e8f0; border-radius: 10px; overflow: auto }
.mm-tabla { width: 100%; border-collapse: collapse; font-size: 13px; min-width: 800px }
.mm-tabla th { text-align: left; padding: 10px 12px; background: #f8fafc; font-weight: 700; color: #475569; font-size: 11px; text-transform: uppercase; letter-spacing: .04em; border-bottom: 1px solid #e2e8f0 }
.mm-tabla td { padding: 8px 12px; border-bottom: 1px solid #f1f5f9; color: #1A3461 }
.mm-tabla code { background: #e0e7ff; color: #3730a3; padding: 1px 6px; border-radius: 3px; font-size: 11.5px }
.mm-loading, .mm-empty { padding: 32px; text-align: center; color: #94a3b8; background: #fff; border: 1px solid #e2e8f0; border-radius: 10px }
.mm-empty strong { display: block; color: #1A3461; margin-bottom: 6px }
.mm-info { margin-top: 18px; padding: 14px 18px; background: #eff6ff; border: 1px solid #bfdbfe; border-radius: 10px; font-size: 12.5px; color: #1e3a8a; line-height: 1.6 }
.mm-info strong { font-size: 13px }
.mm-info ul { margin: 8px 0; padding-left: 22px }
.mm-info b { color: #1e40af }

.mm-btn-del { background: transparent; border: 1px solid #fecaca; color: #b91c1c; padding: 2px 8px; border-radius: 5px; font-weight: 900; cursor: pointer; font-size: 13px; line-height: 1 }
.mm-btn-del:hover { background: #fee2e2 }
`
